import { Icon } from './Icons';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, collection, addDoc, getDocs, orderBy, query, limit, serverTimestamp, updateDoc } from 'firebase/firestore';
import { loadAllBloodMarkers, loadLatestMetrics } from '../db';
import { apiPost } from '../api';
import CompetitionPlans from './CompetitionPlans';
import GenerateWorkout from './GenerateWorkout';
import WOD from './WOD';

// ── Exercise Library ──────────────────────────────────────────────────────────

const EXERCISE_GROUPS = {
  'Lower Body — Quad Dominant': ['Barbell Back Squat','Barbell Front Squat','Safety Bar Squat','Hack Squat (machine)','Leg Press','Bulgarian Split Squat','Lunges (barbell/DB)','Step Ups','Goblet Squat','Leg Extension (machine)'],
  'Lower Body — Posterior Chain': ['Conventional Deadlift','Sumo Deadlift','Romanian Deadlift','Stiff Leg Deadlift','Hip Thrust (barbell)','Hip Thrust (machine)','Glute Bridge','Leg Curl (lying)','Leg Curl (seated)','Nordic Curl','Good Morning','Cable Pull Through'],
  'Upper Body — Push (Horizontal)': ['Barbell Bench Press','Dumbbell Bench Press','Incline Barbell Press','Incline Dumbbell Press','Decline Press','Machine Chest Press','Cable Fly','Dumbbell Fly','Pec Dec (machine)'],
  'Upper Body — Push (Vertical)': ['Barbell OHP','Dumbbell OHP','Arnold Press','Seated Machine Press','Landmine Press','Lateral Raise (DB)','Lateral Raise (cable)','Rear Delt Fly (DB)','Rear Delt Fly (machine)','Face Pull (cable)'],
  'Upper Body — Pull (Horizontal)': ['Barbell Bent Over Row','Dumbbell Row','Cable Row (seated)','Machine Row','Chest Supported Row','Meadows Row','Pendlay Row'],
  'Upper Body — Pull (Vertical)': ['Pull Up (bodyweight)','Weighted Pull Up','Chin Up','Lat Pulldown (bar)','Lat Pulldown (neutral)','Single Arm Pulldown','Straight Arm Pulldown'],
  'Arms': ['Barbell Curl','Dumbbell Curl','Incline Dumbbell Curl','Cable Curl','Hammer Curl','Preacher Curl (machine)','Close Grip Bench Press','Tricep Pushdown (cable)','Overhead Tricep Ext (cable)','Skull Crushers','Dips (tricep)'],
  'Core': ['Plank','Cable Crunch','Hanging Leg Raise','Ab Wheel','Pallof Press','Landmine Rotation'],
  'Calves': ['Standing Calf Raise','Seated Calf Raise','Leg Press Calf Raise'],
};

// ── Gamification Config ───────────────────────────────────────────────────────

const BADGES = [
  { id: 'first_session', icon: 'dumbbell', label: 'First Rep', desc: 'Completed your first session', xp: 100 },
  { id: 'week_streak_1', icon: 'flame', label: 'On Fire', desc: '7-day training streak', xp: 200 },
  { id: 'sessions_5', icon: 'star', label: 'Getting Started', desc: '5 sessions completed', xp: 150 },
  { id: 'sessions_10', icon: 'dumbbell', label: 'Momentum', desc: '10 sessions completed', xp: 250 },
  { id: 'sessions_25', icon: 'award', label: 'Consistent', desc: '25 sessions completed', xp: 500 },
  { id: 'sessions_50', icon: '', label: 'Dedicated', desc: '50 sessions completed', xp: 1000 },
  { id: 'first_pb', icon: 'target', label: 'New Heights', desc: 'First personal best', xp: 200 },
  { id: 'pb_5', icon: 'trending-up', label: 'Progressive', desc: '5 personal bests set', xp: 300 },
  { id: 'pb_20', icon: '', label: 'Unstoppable', desc: '20 personal bests set', xp: 600 },
  { id: 'block_complete', icon: 'calendar', label: 'Block Finisher', desc: 'Completed a 4-week block', xp: 400 },
  { id: 'programme_complete', icon: '', label: 'Programme Graduate', desc: 'Completed the full 12-week programme', xp: 2000 },
];

const XP_LEVELS = [
  { level: 1, label: 'Beginner', xpRequired: 0, color: '#7a9ab8' },
  { level: 2, label: 'Novice', xpRequired: 300, color: '#00c896' },
  { level: 3, label: 'Intermediate', xpRequired: 800, color: '#00AEEF' },
  { level: 4, label: 'Advanced', xpRequired: 1800, color: '#f0a050' },
  { level: 5, label: 'Elite', xpRequired: 3500, color: '#f05050' },
  { level: 6, label: 'Legend', xpRequired: 6000, color: '#a78bfa' },
];

function getLevel(xp) {
  let current = XP_LEVELS[0];
  let next = XP_LEVELS[1];
  for (let i = XP_LEVELS.length - 1; i >= 0; i--) {
    if (xp >= XP_LEVELS[i].xpRequired) { current = XP_LEVELS[i]; next = XP_LEVELS[i + 1] || null; break; }
  }
  return { current, next };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().split('T')[0]; }

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

async function loadTrainingProfile() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'trainingProfile'));
  return snap.exists() ? snap.data() : null;
}

async function saveTrainingProfile(data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'trainingProfile'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

async function loadGamification() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'gamification'));
  return snap.exists() ? snap.data() : { xp: 0, badges: [], totalSessions: 0, totalPBs: 0, streak: 0, lastSessionDate: null };
}

async function saveGamification(data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'gamification'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

async function loadRecentSessions(n = 30) {
  const user = auth.currentUser;
  if (!user) return [];
  const q = query(collection(db, 'users', user.uid, 'history', 'training', 'entries'), orderBy('recordedAt', 'desc'), limit(n));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

async function loadProgramme() {
  const user = auth.currentUser;
  if (!user) return null;
  const snap = await getDoc(doc(db, 'users', user.uid, 'latest', 'programme'));
  return snap.exists() ? snap.data() : null;
}

async function saveProgramme(data) {
  const user = auth.currentUser;
  if (!user) return;
  await setDoc(doc(db, 'users', user.uid, 'latest', 'programme'), { ...data, savedAt: serverTimestamp() });
}

function detectPBs(exercises, sessions) {
  const pbs = [];
  const allLogged = sessions.flatMap(s => s.exercises || []);
  exercises.forEach(ex => {
    if (!ex.name || !ex.weight) return;
    const prev = allLogged.filter(e => e.name === ex.name && e.weight).map(e => parseFloat(e.weight));
    const maxPrev = prev.length ? Math.max(...prev) : 0;
    if (parseFloat(ex.weight) > maxPrev && maxPrev > 0) pbs.push({ exercise: ex.name, weight: ex.weight, prev: maxPrev });
  });
  return pbs;
}

async function processGamification(exercises, sessions, gamification) {
  const pbs = detectPBs(exercises, sessions);
  const totalSessions = (gamification.totalSessions || 0) + 1;
  const totalPBs = (gamification.totalPBs || 0) + pbs.length;
  let xp = (gamification.xp || 0) + 50; // base XP per session
  pbs.forEach(() => { xp += 75; }); // XP per PB

  // Streak calculation
  const lastDate = gamification.lastSessionDate;
  const today = todayStr();
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  let streak = gamification.streak || 0;
  if (lastDate === yesterday || lastDate === today) streak = streak + 1;
  else streak = 1;

  // Check new badges
  const existingBadges = gamification.badges || [];
  const newBadges = [];
  const checkBadge = (id, condition) => {
    if (condition && !existingBadges.includes(id)) {
      newBadges.push(id);
      const badge = BADGES.find(b => b.id === id);
      if (badge) xp += badge.xp;
    }
  };

  checkBadge('first_session', totalSessions >= 1);
  checkBadge('sessions_5', totalSessions >= 5);
  checkBadge('sessions_10', totalSessions >= 10);
  checkBadge('sessions_25', totalSessions >= 25);
  checkBadge('sessions_50', totalSessions >= 50);
  checkBadge('first_pb', totalPBs >= 1);
  checkBadge('pb_5', totalPBs >= 5);
  checkBadge('pb_20', totalPBs >= 20);
  checkBadge('week_streak_1', streak >= 7);

  const updated = { xp, badges: [...existingBadges, ...newBadges], totalSessions, totalPBs, streak, lastSessionDate: today };
  await saveGamification(updated);
  return { updated, pbs, newBadges };
}

function buildLiftLog(sessions) {
  if (!sessions.length) return 'No sessions logged yet.';
  return sessions.slice(0, 15).map(s => {
    const exs = (s.exercises || []).map(e => `${e.name} | ${e.sets}x${e.reps} | ${e.weight ? e.weight + 'kg' : 'BW'} | RPE ${e.rpe || '—'}`).join('\n');
    return `${s.date} — ${s.sessionName}\nWeek ${s.week || '?'} | Block ${s.block || '?'}\n${exs}`;
  }).join('\n\n---\n\n');
}

function buildWorkingWeights(profile) {
  if (!profile?.workingWeights) return 'Not set yet.';
  return Object.entries(profile.workingWeights).filter(([,v]) => v).map(([k,v]) => `${k}: ${v}kg`).join('\n') || 'Not set yet.';
}

// ── UI Components ─────────────────────────────────────────────────────────────

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem', ...style }}>{children}</div>;
}

function SLabel({ children }) {
  return <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>{children}</div>;
}

function Inp({ label, value, onChange, type = 'text', placeholder, style = {} }) {
  return (
    <div style={{ marginBottom: '.65rem', ...style }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
}

function Sel({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: '.65rem' }}>
      {label && <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>{label}</label>}
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .8rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none' }}>
        <option value=''>Select…</option>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
    </div>
  );
}

function Btn({ children, onClick, disabled, variant = 'primary', full = false, style = {} }) {
  const base = { padding: '.65rem 1.25rem', borderRadius: 'var(--radius)', border: 'none', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .6 : 1, transition: 'all .2s', width: full ? '100%' : 'auto' };
  const variants = {
    primary: { background: 'var(--primary)', color: '#080f18' },
    success: { background: 'var(--success)', color: '#080f18' },
    secondary: { background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)' },
    ghost: { background: 'transparent', color: 'var(--primary)', border: '1px solid rgba(0,174,239,.3)' },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>{children}</button>;
}

function XPBar({ xp }) {
  const { current, next } = getLevel(xp);
  const pct = next ? Math.round(((xp - current.xpRequired) / (next.xpRequired - current.xpRequired)) * 100) : 100;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', background: current.color + '22', border: `2px solid ${current.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 700, color: current.color, flexShrink: 0 }}>
        L{current.level}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
          <span style={{ fontSize: '.72rem', fontWeight: 500, color: current.color }}>{current.label}</span>
          <span style={{ fontSize: '.68rem', color: 'var(--text3)' }}>{xp} XP{next ? ` / ${next.xpRequired}` : ''}</span>
        </div>
        <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: current.color, borderRadius: 2, transition: 'width .8s ease' }} />
        </div>
        {next && <div style={{ fontSize: '.65rem', color: 'var(--text3)', marginTop: '.2rem' }}>{next.xpRequired - xp} XP to {next.label}</div>}
      </div>
    </div>
  );
}

function PBToast({ pbs, newBadges, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position: 'fixed', top: 80, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '.5rem', maxWidth: 320 }}>
      {pbs.map((pb, i) => (
        <div key={i} style={{ background: 'rgba(0,200,150,0.95)', borderRadius: 'var(--radius-lg)', padding: '.85rem 1.1rem', color: '#080f18', display: 'flex', alignItems: 'center', gap: '.6rem', backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,200,150,0.3)' }}>
          <Icon name="target" size={14} />
          <div>
            <div style={{ fontSize: '.78rem', fontWeight: 700 }}>Personal Best!</div>
            <div style={{ fontSize: '.72rem' }}>{pb.exercise}: {pb.weight}kg (was {pb.prev}kg)</div>
          </div>
        </div>
      ))}
      {newBadges.map((badgeId, i) => {
        const badge = BADGES.find(b => b.id === badgeId);
        if (!badge) return null;
        return (
          <div key={i} style={{ background: 'rgba(167,139,250,0.95)', borderRadius: 'var(--radius-lg)', padding: '.85rem 1.1rem', color: '#080f18', display: 'flex', alignItems: 'center', gap: '.6rem', backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(167,139,250,0.3)' }}>
            <Icon name={badge.icon} size={16} />
            <div>
              <div style={{ fontSize: '.78rem', fontWeight: 700 }}>Badge Earned! +{badge.xp} XP</div>
              <div style={{ fontSize: '.72rem' }}>{badge.label} — {badge.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Setup Flow ────────────────────────────────────────────────────────────────

function SetupFlow({ onComplete, healthData }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [generatingProg, setGeneratingProg] = useState(false);
  const [profile, setProfile] = useState({
    name: '', goal: '', experience: '', daysPerWeek: '', equipment: '',
    injuries: '', favourites: '', notes: '', exerciseLibrary: {},
    workingWeights: { 'Barbell Back Squat': '', 'Barbell Bench Press': '', 'Conventional Deadlift': '', 'Barbell OHP': '', 'Romanian Deadlift': '', 'Barbell Bent Over Row': '', 'Lat Pulldown (bar)': '' }
  });

  const set = k => v => setProfile(p => ({ ...p, [k]: v }));
  const setLib = (ex, val) => setProfile(p => ({ ...p, exerciseLibrary: { ...p.exerciseLibrary, [ex]: val } }));
  const setWeight = (ex, val) => setProfile(p => ({ ...p, workingWeights: { ...p.workingWeights, [ex]: val } }));

  const steps = ['About You', 'Exercise Library', 'Working Weights', 'Generate Programme'];

  async function generateProgramme(tp) {
    setGeneratingProg(true);
    const yesExercises = Object.entries(tp.exerciseLibrary).filter(([,v]) => v === 'YES').map(([k]) => k);
    const noExercises = Object.entries(tp.exerciseLibrary).filter(([,v]) => v === 'NO').map(([k]) => k);

    let healthContext = '';
    if (healthData?.blood?.systolicBP && parseFloat(healthData.blood.systolicBP) >= 140) healthContext += 'CLINICAL FLAG: Elevated blood pressure — cap RPE at 8, avoid maximal isometrics.\n';
    if (healthData?.mental?.stressScore && parseFloat(healthData.mental.stressScore) >= 70) healthContext += 'CLINICAL FLAG: High stress — reduce volume by 20%.\n';
    if (healthData?.blood?.vitaminD && parseFloat(healthData.blood.vitaminD) < 30) healthContext += 'CLINICAL FLAG: Low Vitamin D — note increased injury risk.\n';

    const prompt = `You are an elite personal trainer building a complete 12-week periodised programme.

ATHLETE PROFILE:
Name: ${tp.name}
Goal: ${tp.goal}
Experience: ${tp.experience}
Days per week: ${tp.daysPerWeek}
Equipment: ${tp.equipment}
Injuries/limitations: ${tp.injuries || 'None'}
Favourite exercises: ${tp.favourites || 'Not specified'}
Notes: ${tp.notes || ''}

APPROVED EXERCISES (use ONLY these):
${yesExercises.length > 0 ? yesExercises.join(', ') : 'Standard exercises for the equipment available'}

EXERCISES TO AVOID:
${noExercises.length > 0 ? noExercises.join(', ') : 'None'}

CURRENT WORKING WEIGHTS:
${Object.entries(tp.workingWeights).filter(([,v])=>v).map(([k,v])=>k+': '+v+'kg').join('\n') || 'Calibration week — use conservative starting weights'}

HEALTH CONSIDERATIONS:
${healthContext || 'No clinical flags — full training capacity.'}

PROGRAMME STRUCTURE:
- 12 weeks total, 3 blocks of 4 weeks each
- Week 12 = deload (50% volume, same movements)
- Block 1 (Weeks 1-4): Foundation — higher reps, technique focus, RPE 7-8
- Block 2 (Weeks 5-8): Build — moderate reps, progressive load, RPE 8-9
- Block 3 (Weeks 9-11): Peak — lower reps, heavier loads, RPE 8-9

PROGRAMMING RULES:
- RPE 7-8 for hypertrophy, RPE 8-9 for strength
- Use working weights as starting point, never % of 1RM
- Week 1 = calibration week (start conservative)
- Include warm-up sets guidance
- Progressive overload each week within blocks
- EVERY SESSION must include at least 2 core exercises (planks, dead bugs, pallof press, hanging leg raises, cable crunches, ab wheel — vary them each session)
- EVERY WEEK must include at least one cardio element (10-15 min cardio finisher on at least 2 sessions, or a dedicated cardio day if days allow)
- Core and cardio are non-negotiable — do not omit them under any circumstances

Return a JSON object ONLY (no markdown, no explanation) in this EXACT format:
{
  "programmeTitle": "string",
  "goal": "string",
  "weeks": [
    {
      "week": 1,
      "block": 1,
      "blockLabel": "Foundation",
      "isDeload": false,
      "sessions": [
        {
          "day": 1,
          "sessionName": "Upper A — Chest & Back",
          "focus": "Chest, Back",
          "warmup": "5 min incline treadmill walk, band pull aparts x15, arm circles",
          "exercises": [
            {
              "name": "Barbell Bench Press",
              "sets": 4,
              "reps": "8-10",
              "rpe": 7,
              "restSeconds": 120,
              "weight": "Use working weight or conservative start",
              "coachingCue": "Control the descent, drive through full range"
            }
          ],
          "coreBlock": [
            {
              "name": "Dead Bug",
              "sets": 3,
              "reps": "10 each side",
              "restSeconds": 30,
              "coachingCue": "Press lower back into floor, extend opposite arm and leg slowly"
            },
            {
              "name": "Pallof Press",
              "sets": 3,
              "reps": "12 each side",
              "restSeconds": 30,
              "coachingCue": "Resist rotation, brace through the entire movement"
            }
          ],
          "cardioFinisher": {
            "description": "10 min assault bike Zone 2 — maintain conversational pace, RPE 5-6. Alternatively: 10 min incline treadmill walk at 6% / 5 kmh"
          },
          "cooldown": "Chest stretch 30s, lat stretch 30s, hip flexor stretch 30s each side"
        }
      ]
    }
  ]
}

Build all 12 weeks fully. Each session must have 4-6 main exercises, 2-3 core exercises, and 1 cardio finisher. Make it genuinely excellent — like a top PT built it.`;

    try {
      const data = await apiPost('/api/generate-programme', { prompt });
      const prog = typeof data.programme === 'string' ? JSON.parse(data.programme) : data.programme;
      await saveProgramme(prog);
      setGeneratingProg(false);
      return prog;
    } catch (e) {
      console.error('Programme generation error:', e);
      setGeneratingProg(false);
      setGenError(e.message === 'RATE_LIMIT' ? 'Rate limit reached. Please try again in an hour.' : 'Generation failed. Please try again in a moment.');
      return null;
    }
  }

  async function handleFinish() {
    setSaving(true);
    try {
      const tp = { ...profile, setupComplete: true, startDate: todayStr(), currentWeek: 1, currentBlock: 1 };
      await saveTrainingProfile(tp);
      const prog = await generateProgramme(tp);
      onComplete(tp, prog);
    } catch (e) { alert('Error saving. Please try again.'); }
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', marginBottom: '.3rem' }}>Set Up Your Training</h1>
        <p style={{ fontSize: '.8rem', color: 'var(--text3)', fontWeight: 300 }}>Tell Rico PT about you — it will build a complete 12-week programme personalised to your goals and health data.</p>
      </div>

      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '2rem' }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1 }}>
            <div style={{ height: 3, borderRadius: 2, background: i <= step ? 'var(--primary)' : 'var(--border2)', marginBottom: '.4rem', transition: 'background .3s' }} />
            <div style={{ fontSize: '.65rem', color: i === step ? 'var(--primary)' : 'var(--text3)', fontWeight: i === step ? 600 : 300, textAlign: 'center' }}>{s}</div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card>
          <SLabel>Your Training Profile</SLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
            <Inp label="Your Name" value={profile.name} onChange={set('name')} placeholder="First name" />
            <Sel label="Training Goal" value={profile.goal} onChange={set('goal')} options={['Hypertrophy (muscle building)','Strength','Fat loss','General fitness','Athletic performance']} />
            <Sel label="Experience Level" value={profile.experience} onChange={set('experience')} options={['Beginner (0-1 years)','Intermediate (1-3 years)','Advanced (3+ years)']} />
            <Sel label="Days Per Week" value={profile.daysPerWeek} onChange={set('daysPerWeek')} options={['2','3','4','5','6']} />
            <Sel label="Equipment Access" value={profile.equipment} onChange={set('equipment')} options={['Commercial gym (full)','Home gym (barbell + rack)','Dumbbells only','Cables + machines only','Bodyweight only']} />
          </div>
          <Inp label="Injuries or Limitations" value={profile.injuries} onChange={set('injuries')} placeholder="e.g. Left shoulder impingement, avoid behind neck press" />
          <Inp label="Favourite Exercises" value={profile.favourites} onChange={set('favourites')} placeholder="e.g. Bench press, RDL, pull ups" />
          <Inp label="Other Notes for Rico PT" value={profile.notes} onChange={set('notes')} placeholder="e.g. Train early morning, prefer less technical movements first" />
          {healthData && (
            <div style={{ padding: '.85rem 1rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              <Icon name="info" size={14} /> Rico will also use your Levare health data (blood markers, BMI, stress) to personalise your programme and flag any training contraindications.
            </div>
          )}
        </Card>
      )}

      {step === 1 && (
        <Card>
          <SLabel>Exercise Library</SLabel>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Mark each exercise: YES (include in my programme), SUB (can do but prefer alternatives), NO (avoid entirely). Rico will only use YES exercises.
          </p>
          {Object.entries(EXERCISE_GROUPS).map(([group, exercises]) => (
            <div key={group} style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.6rem' }}>{group}</div>
              {exercises.map(ex => (
                <div key={ex} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.4rem .75rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.25rem' }}>
                  <span style={{ fontSize: '.8rem', color: 'var(--text2)', fontWeight: 300 }}>{ex}</span>
                  <div style={{ display: 'flex', gap: '.3rem' }}>
                    {['YES', 'SUB', 'NO'].map(opt => {
                      const sel = profile.exerciseLibrary[ex] === opt;
                      const colors = { YES: 'var(--success)', SUB: 'var(--warning)', NO: 'var(--danger)' };
                      const dims = { YES: 'var(--success-dim)', SUB: 'var(--warning-dim)', NO: 'var(--danger-dim)' };
                      return (
                        <button key={opt} onClick={() => setLib(ex, opt)} style={{ padding: '.2rem .55rem', borderRadius: 4, border: `1px solid ${sel ? colors[opt] : 'var(--border2)'}`, fontSize: '.65rem', fontWeight: 600, cursor: 'pointer', background: sel ? dims[opt] : 'transparent', color: sel ? colors[opt] : 'var(--text3)' }}>{opt}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </Card>
      )}

      {step === 2 && (
        <Card>
          <SLabel>Current Working Weights</SLabel>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Enter your current working weights — the actual weight you lift in sessions, not your 1 rep max. Leave blank if unsure; Rico will calibrate in Week 1.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
            {Object.keys(profile.workingWeights).map(ex => (
              <Inp key={ex} label={ex + ' (kg)'} type="number" value={profile.workingWeights[ex]} onChange={v => setWeight(ex, v)} placeholder="e.g. 80" />
            ))}
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            {generatingProg ? (
              <>
                <div style={{ width: 56, height: 56, borderRadius: '50%', border: '3px solid rgba(0,174,239,0.2)', borderTop: '3px solid var(--primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.5rem' }} />
                <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: 'var(--text)', marginBottom: '.5rem' }}>Building your programme…</div>
                <p style={{ fontSize: '.8rem', color: 'var(--text3)', fontWeight: 300 }}>Rico PT is creating a complete 12-week periodised programme based on your profile, exercise preferences and health data. This takes about 30 seconds.</p>
              </>
            ) : (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><Icon name="dumbbell" size={14} /></div>
                <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.4rem', color: 'var(--text)', marginBottom: '.75rem' }}>Ready to build your programme</div>
                <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7, maxWidth: 440, margin: '0 auto 1.5rem' }}>
                  Rico PT will generate a complete 12-week programme — every session, every exercise, pre-loaded and ready to go. All you need to do is show up and log your sets.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', maxWidth: 360, margin: '0 auto 1.5rem', textAlign: 'left' }}>
                  {[`Goal: ${profile.goal||'Not set'}`, `Experience: ${profile.experience||'Not set'}`, `${profile.daysPerWeek||'?'} days/week`, `Equipment: ${profile.equipment||'Not set'}`].map(item => (
                    <div key={item} style={{ padding: '.5rem .85rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--text2)' }}>{item}</div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'flex-end' }}>
        {step > 0 && !generatingProg && <Btn variant="secondary" onClick={() => setStep(s => s-1)}>← Back</Btn>}
        {step < steps.length - 1 && <Btn onClick={() => setStep(s => s+1)}>Continue →</Btn>}
        {step === steps.length - 1 && !generatingProg && <Btn onClick={handleFinish} disabled={saving}>{saving ? 'Generating…' : 'Build My Programme →'}</Btn>}
      </div>
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

// ── Guided Workout ────────────────────────────────────────────────────────────


// ── Exercise Description Database ────────────────────────────────────────────
const EXERCISE_DB = {
  "squat": { muscles: "Quads, glutes, hamstrings", how: "Stand with feet shoulder-width apart. Brace your core and sit back and down as if sitting into a chair. Keep chest tall and knees tracking over toes. Drive through the heels to stand.", tips: "Do not let knees cave inward. Depth should be at least parallel.", equipment: "Barbell / Dumbbell / Bodyweight" },
  "deadlift": { muscles: "Hamstrings, glutes, lower back, traps", how: "Stand with feet hip-width, bar over mid-foot. Hinge at hips, grip the bar just outside knees. Brace core, push floor away to lift. Lock hips and shoulders out together.", tips: "Keep the bar close to your body throughout. Do not round the lower back.", equipment: "Barbell / Dumbbell" },
  "romanian deadlift": { muscles: "Hamstrings, glutes, lower back", how: "Hold bar at hip height. Hinge at hips with slight knee bend, pushing hips back as you lower the bar down your shins. Feel a stretch in hamstrings, then drive hips forward to return.", tips: "Keep back flat throughout. This is a hip hinge, not a squat.", equipment: "Barbell / Dumbbell" },
  "rdl": { muscles: "Hamstrings, glutes, lower back", how: "Hold bar at hip height. Hinge at hips with slight knee bend, pushing hips back as you lower. Feel a stretch in hamstrings, then drive hips forward to return.", tips: "Keep back flat. Push your hips back rather than just bending forward.", equipment: "Barbell / Dumbbell" },
  "leg press": { muscles: "Quads, glutes, hamstrings", how: "Sit in machine, feet shoulder-width on platform. Lower weight by bending knees to 90 degrees, then press back up. Do not lock knees at the top.", tips: "Higher foot placement targets glutes more. Lower targets quads.", equipment: "Machine" },
  "leg curl": { muscles: "Hamstrings", how: "Lie face down on machine. Curl your heels towards your glutes against resistance. Lower slowly under control.", tips: "Do not swing or use momentum. Full range of motion.", equipment: "Machine" },
  "leg extension": { muscles: "Quadriceps", how: "Sit in machine, pad resting on shins just above ankles. Extend legs until straight, squeeze quads at top, lower slowly.", tips: "Keep back pressed into the seat. Do not hyperextend at the top.", equipment: "Machine" },
  "lunge": { muscles: "Quads, glutes, hamstrings", how: "Step forward with one foot. Lower back knee towards floor, keeping front shin vertical. Drive front foot into floor to return.", tips: "Keep torso upright. Do not let front knee travel past your toes.", equipment: "Bodyweight / Dumbbell / Barbell" },
  "bulgarian split squat": { muscles: "Quads, glutes, hip flexors", how: "Rear foot elevated on bench, front foot forward. Lower rear knee towards floor. Keep chest tall and front knee over toes.", tips: "Front foot distance from bench affects muscle emphasis — further means more glutes.", equipment: "Bodyweight / Dumbbell" },
  "hip thrust": { muscles: "Glutes, hamstrings", how: "Upper back on bench, barbell across hips. Drive hips up until body is straight from knees to shoulders. Squeeze glutes hard at top.", tips: "Chin tucked to chest. Full hip extension at the top.", equipment: "Barbell / Bodyweight" },
  "glute bridge": { muscles: "Glutes, hamstrings", how: "Lie on back, feet flat on floor. Drive hips up by squeezing glutes, hold briefly at top, lower slowly.", tips: "Press through your heels, not your toes.", equipment: "Bodyweight / Barbell" },
  "calf raise": { muscles: "Calves (gastrocnemius, soleus)", how: "Stand with balls of feet on edge of step. Lower heels below the step, then rise onto toes as high as possible. Lower slowly.", tips: "Full range of motion matters here — really stretch at the bottom.", equipment: "Machine / Bodyweight" },
  "bench press": { muscles: "Chest, shoulders, triceps", how: "Lie on bench, grip bar slightly wider than shoulder-width. Lower bar to mid-chest, touch lightly, then press back up. Keep wrists stacked over elbows.", tips: "Keep feet flat on floor and back slightly arched. Do not bounce bar off chest.", equipment: "Barbell / Dumbbell" },
  "incline bench press": { muscles: "Upper chest, shoulders, triceps", how: "Same as bench press but on inclined bench at 30 to 45 degrees. Bar should lower to upper chest.", tips: "The angle shifts emphasis to upper chest and front delts.", equipment: "Barbell / Dumbbell" },
  "overhead press": { muscles: "Shoulders, triceps, upper chest", how: "Stand or sit with bar at shoulder height, grip just outside shoulders. Press bar straight overhead. Lock elbows at top.", tips: "Brace core hard. Do not hyperextend your lower back.", equipment: "Barbell / Dumbbell" },
  "ohp": { muscles: "Shoulders, triceps, upper chest", how: "Stand or sit with bar at shoulder height, grip just outside shoulders. Press bar straight overhead. Lock elbows at top.", tips: "Brace core hard. Do not hyperextend your lower back.", equipment: "Barbell / Dumbbell" },
  "dumbbell press": { muscles: "Chest, shoulders, triceps", how: "Lie on bench holding dumbbells at chest. Press up until arms are extended. Lower slowly to chest height.", tips: "Greater range of motion than barbell. Keep wrists neutral.", equipment: "Dumbbell" },
  "push up": { muscles: "Chest, shoulders, triceps, core", how: "Hands shoulder-width, body in straight line from head to heels. Lower chest to floor, push back up. Keep core tight throughout.", tips: "Squeeze your glutes and abs to prevent sagging hips.", equipment: "Bodyweight" },
  "dips": { muscles: "Triceps, lower chest, shoulders", how: "Support yourself on parallel bars. Lower body by bending elbows until upper arms are parallel to floor. Press back up.", tips: "Lean forward for more chest focus; stay upright for more triceps.", equipment: "Parallel bars / Machine" },
  "lateral raise": { muscles: "Side deltoids", how: "Hold dumbbells at sides. Raise arms out to the sides until shoulder height. Lower slowly with control.", tips: "Slight bend in elbows. Lead with elbows, not hands. Go lighter than you think.", equipment: "Dumbbell / Cable" },
  "front raise": { muscles: "Front deltoids", how: "Hold dumbbells at front of thighs. Raise arms in front to shoulder height. Lower slowly.", tips: "Keep a slight bend in elbows. Do not swing.", equipment: "Dumbbell / Barbell" },
  "pull up": { muscles: "Lats, biceps, rear delts", how: "Hang from bar with overhand grip, shoulder-width. Pull yourself up until chin clears the bar. Lower with control.", tips: "Initiate by pulling shoulder blades down. Do not kip unless intended.", equipment: "Pull-up bar" },
  "chin up": { muscles: "Lats, biceps, rear delts", how: "Hang from bar with underhand grip, shoulder-width. Pull yourself up until chin clears the bar. Lower with control.", tips: "Underhand grip recruits more biceps than pull-up.", equipment: "Pull-up bar" },
  "lat pulldown": { muscles: "Lats, biceps, rear delts", how: "Sit at cable machine, overhand grip on bar. Pull bar down to upper chest while leaning slightly back. Return bar slowly overhead.", tips: "Drive elbows down and back. Imagine bending the bar.", equipment: "Cable machine" },
  "seated row": { muscles: "Middle back, lats, biceps", how: "Sit at cable row machine, feet on platform. Pull handle to lower abdomen, squeezing shoulder blades together. Return with control.", tips: "Keep back upright. Lead with elbows, not hands.", equipment: "Cable machine" },
  "bent over row": { muscles: "Back, lats, biceps, rear delts", how: "Hinge forward at hips, back flat. Pull bar to lower ribcage. Lower slowly. Keep core braced throughout.", tips: "Do not round your back. Squeeze shoulder blades together at the top.", equipment: "Barbell / Dumbbell" },
  "face pull": { muscles: "Rear delts, rotator cuff, traps", how: "Cable at face height. Pull rope to your face, elbows flaring out. Pause at full rotation, return slowly.", tips: "Key for shoulder health — do not neglect this. High reps work well.", equipment: "Cable machine" },
  "shrug": { muscles: "Traps", how: "Hold weight at sides. Shrug shoulders straight up towards ears. Hold briefly, lower slowly.", tips: "Do not roll your shoulders — straight up and down only.", equipment: "Barbell / Dumbbell" },
  "bicep curl": { muscles: "Biceps", how: "Hold dumbbells at sides, palms facing forward. Curl weights up by bending elbows. Lower slowly.", tips: "Keep elbows pinned to your sides. Do not swing.", equipment: "Dumbbell / Barbell / Cable" },
  "hammer curl": { muscles: "Biceps, brachialis", how: "Hold dumbbells with neutral grip (palms facing each other). Curl up keeping neutral grip throughout.", tips: "Great for forearm development alongside biceps.", equipment: "Dumbbell" },
  "tricep pushdown": { muscles: "Triceps", how: "Cable machine, rope or bar attachment. Push down until arms fully extended, elbows at sides. Return slowly.", tips: "Keep elbows pinned to your sides. Do not flare them.", equipment: "Cable machine" },
  "skull crusher": { muscles: "Triceps", how: "Lie on bench holding bar above chest. Lower bar towards forehead by bending elbows only. Extend back up.", tips: "Keep upper arms vertical throughout the movement.", equipment: "Barbell / EZ bar / Dumbbell" },
  "tricep dip": { muscles: "Triceps, chest", how: "Hands on bench or chair behind you. Lower body by bending elbows to 90 degrees, then press back up.", tips: "Keep hips close to the bench. Do not let shoulders hunch up.", equipment: "Bench / Chair" },
  "plank": { muscles: "Core, shoulders, glutes", how: "Hold push-up position on forearms. Keep body in straight line from head to heels. Breathe normally.", tips: "Squeeze glutes and abs. Do not let hips sag or rise.", equipment: "Bodyweight" },
  "crunch": { muscles: "Abs", how: "Lie on back, knees bent. Lift shoulders off floor by contracting abs. Lower slowly.", tips: "Do not pull on neck. The movement should be small and controlled.", equipment: "Bodyweight" },
  "russian twist": { muscles: "Obliques, core", how: "Sit on floor, knees bent, lean back slightly. Rotate torso side to side, tapping the floor.", tips: "Keep feet elevated for more difficulty. The rotation comes from your torso, not your arms.", equipment: "Bodyweight / Dumbbell / Plate" },
  "leg raise": { muscles: "Lower abs, hip flexors", how: "Lie on back, legs straight. Raise legs to 90 degrees keeping them straight. Lower slowly without touching floor.", tips: "Press lower back into the floor throughout.", equipment: "Bodyweight" },
  "mountain climber": { muscles: "Core, shoulders, hip flexors", how: "Push-up position. Drive one knee towards chest, then alternate rapidly as if running in place.", tips: "Keep hips level. Do not let them rock side to side.", equipment: "Bodyweight" },
  "burpee": { muscles: "Full body", how: "From standing, drop hands to floor, jump feet back to push-up position, do a push-up, jump feet forward, jump up with hands overhead.", tips: "Scale by removing the jump or push-up. Focus on consistent pace.", equipment: "Bodyweight" },
  "box jump": { muscles: "Quads, glutes, calves", how: "Stand facing box. Swing arms and jump onto the box, landing softly with bent knees. Step back down.", tips: "Land softly — absorb the impact through your legs. Step down, do not jump down.", equipment: "Box / Plyo box" },
};


function getExerciseInfo(name) {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  // Direct match
  if (EXERCISE_DB[key]) return EXERCISE_DB[key];
  // Check if any DB key appears as a word in the exercise name
  const match = Object.keys(EXERCISE_DB).find(k => {
    // "deadlift" matches "Barbell Deadlift", "Romanian Deadlift" etc
    return key.includes(k);
  });
  // If no match, try matching on individual words of the exercise name
  if (!match) {
    const words = key.split(' ');
    const wordMatch = Object.keys(EXERCISE_DB).find(k =>
      words.some(w => w.length > 3 && k.includes(w))
    );
    return wordMatch ? EXERCISE_DB[wordMatch] : null;
  }
  return match ? EXERCISE_DB[match] : null;
}

function GuidedWorkout({ session, week, block, sessions, trainingProfile, onComplete, onCancel }) {
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [loggedSets, setLoggedSets] = useState(
    (session.exercises || []).map(ex => Array.from({ length: ex.sets }, () => ({ reps: '', weight: '', done: false })))
  );
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [timer, setTimer] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [timerVal, setTimerVal] = useState(0);
  const timerRef = useRef(null);

  const exercises = session.exercises || [];
  const currentEx = exercises[exerciseIndex] || {};
  const currentSets = loggedSets[exerciseIndex] || [];
  const totalExercises = exercises.length;
  const completedExercises = loggedSets.filter((sets, i) => sets.every(s => s.done)).length;

  function startTimer(seconds) {
    setTimerVal(seconds);
    setTimerActive(true);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimerVal(v => {
        if (v <= 1) { clearInterval(timerRef.current); setTimerActive(false); return 0; }
        return v - 1;
      });
    }, 1000);
  }

  useEffect(() => () => clearInterval(timerRef.current), []);

  function markSetDone(exIdx, setIdx) {
    setLoggedSets(prev => {
      const sets = prev[exIdx];
      const doneWeight = sets[setIdx].weight;
      const updated = prev.map((s, ei) => {
        if (ei !== exIdx) return s;
        return s.map((set, si) => {
          if (si === setIdx) return { ...set, done: true };
          // Auto-fill weight from completed set into empty subsequent sets
          if (si > setIdx && !set.weight && doneWeight) return { ...set, weight: doneWeight };
          return set;
        });
      });
      return updated;
    });
    if (currentEx.restSeconds) startTimer(currentEx.restSeconds);
  }

  function updateSet(exIdx, setIdx, field, value) {
    setLoggedSets(prev => prev.map((sets, ei) => ei !== exIdx ? sets : sets.map((s, si) => si !== setIdx ? s : { ...s, [field]: value })));
  }

  async function handleFinish() {
    setSaving(true);
    try {
      const exercisesLogged = exercises.map((ex, i) => ({
        name: ex.name,
        sets: currentSets.filter(s => s.done).length || ex.sets,
        reps: loggedSets[i]?.find(s => s.reps)?.reps || ex.reps,
        weight: loggedSets[i]?.find(s => s.weight)?.weight || '',
        rpe: ex.rpe || '',
        notes: ex.coachingCue || ''
      }));

      const sessionData = {
        date: todayStr(), sessionName: session.sessionName, week, block,
        sessionNotes, exercises: exercisesLogged, fromProgramme: true
      };

      const user = auth.currentUser;
      await addDoc(collection(db, 'users', user.uid, 'history', 'training', 'entries'), { ...sessionData, recordedAt: serverTimestamp() });

      const gamSnap = await getDoc(doc(db, 'users', user.uid, 'latest', 'gamification'));
      const gam = gamSnap.exists() ? gamSnap.data() : { xp: 0, badges: [], totalSessions: 0, totalPBs: 0, streak: 0 };
      const { updated, pbs, newBadges } = await processGamification(exercisesLogged, sessions, gam);

      // Update working weights
      if (trainingProfile) {
        const updatedWeights = { ...(trainingProfile.workingWeights || {}) };
        exercisesLogged.forEach(ex => {
          if (ex.weight && updatedWeights.hasOwnProperty(ex.name)) {
            if (!updatedWeights[ex.name] || parseFloat(ex.weight) > parseFloat(updatedWeights[ex.name])) updatedWeights[ex.name] = ex.weight;
          }
        });
        await saveTrainingProfile({ ...trainingProfile, workingWeights: updatedWeights });
      }

      onComplete({ pbs, newBadges, gamification: updated });
    } catch (e) { console.error(e); alert('Error saving session.'); }
    setSaving(false);
  }

  const progressPct = Math.round((completedExercises / totalExercises) * 100);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.2rem' }}>Week {week} · Block {block}</div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)' }}>{session.sessionName}</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          {timerActive && (
            <div style={{ padding: '.5rem 1rem', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.3)', borderRadius: 'var(--radius)', fontSize: '.9rem', fontWeight: 600, color: 'var(--warning)', fontFamily: 'monospace' }}>
              Rest: {Math.floor(timerVal/60)}:{String(timerVal%60).padStart(2,'0')}
            </div>
          )}
          <Btn variant="secondary" onClick={onCancel}>Exit</Btn>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.3rem' }}>
          <span style={{ fontSize: '.7rem', color: 'var(--text3)' }}>{completedExercises}/{totalExercises} exercises</span>
          <span style={{ fontSize: '.7rem', color: 'var(--success)', fontWeight: 500 }}>{progressPct}% complete</span>
        </div>
        <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: progressPct + '%', height: '100%', background: 'var(--success)', borderRadius: 3, transition: 'width .5s ease' }} />
        </div>
      </div>

      {/* Warmup */}
      {session.warmup && (
        <div style={{ padding: '.75rem 1rem', background: 'rgba(240,160,80,0.06)', border: '1px solid rgba(240,160,80,0.2)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--warning)', marginBottom: '1rem' }}>
          <Icon name="flame" size={14} /> Warm-up: {session.warmup}
        </div>
      )}

      {/* Exercise navigator */}
      <div style={{ display: 'flex', gap: '.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {exercises.map((ex, i) => {
          const done = loggedSets[i]?.every(s => s.done);
          return (
            <button key={i} onClick={() => { setExerciseIndex(i); setShowInfo(false); }} style={{ padding: '.35rem .75rem', borderRadius: 'var(--radius)', border: '1px solid', borderColor: i === exerciseIndex ? 'var(--primary)' : done ? 'var(--success)' : 'var(--border2)', background: i === exerciseIndex ? 'rgba(0,174,239,0.1)' : done ? 'var(--success-dim)' : 'transparent', color: i === exerciseIndex ? 'var(--primary)' : done ? 'var(--success)' : 'var(--text3)', fontSize: '.7rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              {done ? '✓ ' : ''}{i + 1}. {ex.name.split(' ').slice(0,2).join(' ')}
            </button>
          );
        })}
      </div>

      {/* Current exercise */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>{currentEx.name}</div>
            <div style={{ display: 'flex', gap: '.75rem', fontSize: '.75rem', color: 'var(--text3)' }}>
              <span>{currentEx.sets} sets × {currentEx.reps} reps</span>
              {currentEx.rpe && <span>Target RPE {currentEx.rpe}</span>}
              {currentEx.restSeconds && <span>Rest {currentEx.restSeconds}s</span>}
            </div>
          </div>
          <div style={{ padding: '.35rem .75rem', background: 'rgba(0,174,239,0.1)', border: '1px solid rgba(0,174,239,0.2)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--primary)', fontWeight: 500 }}>
            {exerciseIndex + 1} of {totalExercises}
          </div>
        </div>

        {currentEx.coachingCue && (
          <div style={{ padding: '.65rem .85rem', background: 'rgba(0,174,239,0.05)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', marginBottom: '.75rem', lineHeight: 1.5 }}>
            <Icon name="message" size={14} /> <strong style={{ color: 'var(--primary)' }}>Coach: </strong>{currentEx.coachingCue}
          </div>
        )}
        {(() => {
          const info = getExerciseInfo(currentEx.name);
          return (
            <div style={{ marginBottom: '1rem' }}>
              <button onClick={() => setShowInfo(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '.72rem', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0, fontWeight: 500 }}>
                <span style={{ fontSize: '.6rem' }}>{showInfo ? '▲' : '▼'}</span> {showInfo ? 'Hide exercise guide' : ' How to perform this exercise'}
              </button>
              {showInfo && (
                <div style={{ marginTop: '.5rem', padding: '.85rem 1rem', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '.78rem', lineHeight: 1.7 }}>
                  {info ? (
                    <>
                      <div style={{ marginBottom: '.4rem' }}><strong style={{ color: 'var(--text)', fontWeight: 600 }}>Muscles: </strong><span style={{ color: 'var(--text3)' }}>{info.muscles}</span></div>
                      <div style={{ marginBottom: '.4rem' }}><strong style={{ color: 'var(--text)', fontWeight: 600 }}>How to: </strong><span style={{ color: 'var(--text3)' }}>{info.how}</span></div>
                      <div style={{ marginBottom: '.4rem' }}><strong style={{ color: 'var(--warning)', fontWeight: 600 }}>Tip: </strong><span style={{ color: 'var(--text3)' }}>{info.tips}</span></div>
                      <div><strong style={{ color: 'var(--text)', fontWeight: 600 }}>Equipment: </strong><span style={{ color: 'var(--text3)' }}>{info.equipment}</span></div>
                    </>
                  ) : (
                    <div style={{ color: 'var(--text3)' }}>
                      No guide available for "{currentEx.name}". Ask Rico PT for coaching tips on this exercise.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Sets table */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 1fr 80px', gap: '.4rem', marginBottom: '.4rem' }}>
            {['Set', 'Target', 'Weight (kg)', 'Reps done', ''].map(h => (
              <div key={h} style={{ fontSize: '.62rem', color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</div>
            ))}
          </div>
          {currentSets.map((s, si) => (
            <div key={si} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 1fr 80px', gap: '.4rem', marginBottom: '.35rem', alignItems: 'center', opacity: s.done ? 0.6 : 1 }}>
              <div style={{ fontSize: '.82rem', color: s.done ? 'var(--success)' : 'var(--text3)', fontWeight: 600 }}>{s.done ? '✓' : si + 1}</div>
              <div style={{ fontSize: '.78rem', color: 'var(--text3)' }}>{currentEx.reps} @ RPE {currentEx.rpe}</div>
              <input type="number" value={s.weight} onChange={e => updateSet(exerciseIndex, si, 'weight', e.target.value)}
                placeholder='kg' disabled={s.done}
                style={{ background: 'var(--bg-elevated)', border: `1px solid ${s.done ? 'var(--success)' : 'var(--border2)'} `, borderRadius: 'var(--radius)', padding: '.45rem .6rem', color: 'var(--text)', fontSize: '.82rem', outline: 'none', width: '100%' }} />
              <input type="text" value={s.reps} onChange={e => updateSet(exerciseIndex, si, 'reps', e.target.value)}
                placeholder={currentEx.reps} disabled={s.done}
                style={{ background: 'var(--bg-elevated)', border: `1px solid ${s.done ? 'var(--success)' : 'var(--border2)'}`, borderRadius: 'var(--radius)', padding: '.45rem .6rem', color: 'var(--text)', fontSize: '.82rem', outline: 'none', width: '100%' }} />
              {!s.done ? (
                <Btn variant="success" onClick={() => markSetDone(exerciseIndex, si)} style={{ padding: '.45rem .6rem', fontSize: '.72rem' }}>Done ✓</Btn>
              ) : (
                <div style={{ fontSize: '.72rem', color: 'var(--success)', textAlign: 'center' }}>✓</div>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '.75rem', marginTop: '1.25rem' }}>
          {exerciseIndex > 0 && <Btn variant="secondary" onClick={() => { setExerciseIndex(i => i-1); setShowInfo(false); }}>← Previous</Btn>}
          {exerciseIndex < totalExercises - 1 ? (
            <Btn onClick={() => { setExerciseIndex(i => i+1); setShowInfo(false); }} style={{ marginLeft: 'auto' }}>Next Exercise →</Btn>
          ) : (
            <div style={{ marginLeft: 'auto' }}>
              <Btn variant="success" onClick={handleFinish} disabled={saving}>{saving ? 'Saving…' : ' Finish Session'}</Btn>
            </div>
          )}
        </div>
      </Card>

      {/* Cooldown */}
      {session.cooldown && (
        <div style={{ padding: '.75rem 1rem', background: 'rgba(0,200,150,0.06)', border: '1px solid rgba(0,200,150,0.2)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--success)' }}>
           Cool-down: {session.cooldown}
        </div>
      )}

      {/* Core Block */}
      {session.coreBlock && session.coreBlock.length > 0 && (
        <Card style={{ borderColor: 'rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.03)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1rem' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#a78bfa' }} />
            <SLabel style={{ margin: 0 }}>Core Block</SLabel>
            <span style={{ fontSize: '.65rem', color: '#a78bfa', fontWeight: 300 }}>Complete after main work · minimal rest</span>
          </div>
          {session.coreBlock.map((ex, i) => (
            <div key={i} style={{ padding: '.85rem 0', borderBottom: i < session.coreBlock.length - 1 ? '1px solid var(--border2)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.3rem' }}>
                <div style={{ fontSize: '.88rem', fontWeight: 500, color: 'var(--text)' }}>{ex.name}</div>
                <div style={{ fontSize: '.78rem', color: '#a78bfa', fontWeight: 600 }}>{ex.sets} × {ex.reps}</div>
              </div>
              {ex.coachingCue && (
                <div style={{ fontSize: '.73rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.55 }}>{ex.coachingCue}</div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Cardio Finisher */}
      {session.cardioFinisher && session.cardioFinisher.description && (
        <Card style={{ borderColor: 'rgba(0,200,150,0.3)', background: 'rgba(0,200,150,0.03)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)' }} />
            <SLabel style={{ margin: 0 }}>Cardio Finisher</SLabel>
          </div>
          <div style={{ fontSize: '.85rem', color: 'var(--text2)', fontWeight: 300, lineHeight: 1.65 }}>{session.cardioFinisher.description}</div>
        </Card>
      )}

      {/* Session notes */}
      <Card style={{ marginTop: '1rem' }}>
        <SLabel>Session Notes</SLabel>
        <textarea value={sessionNotes} onChange={e => setSessionNotes(e.target.value)}
          placeholder="Energy levels, how it felt, anything notable…" rows={2}
          style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.65rem .85rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
      </Card>
    </div>
  );
}

// ── Programme View ────────────────────────────────────────────────────────────

function ProgrammeView({ programme, trainingProfile, sessions, gamification, onStartSession }) {
  const [selectedWeek, setSelectedWeek] = useState(trainingProfile?.currentWeek || 1);
  const [selectedDay, setSelectedDay] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  async function handleGenerate() {
    setGenerating(true);
    setGenError('');
    try {
      const yesExercises = Object.entries(trainingProfile?.exerciseLibrary || {}).filter(([,v]) => v === 'YES').map(([k]) => k);
      const noExercises = Object.entries(trainingProfile?.exerciseLibrary || {}).filter(([,v]) => v === 'NO').map(([k]) => k);
      const prompt = `You are an elite personal trainer. Build a complete 12-week periodised programme for this athlete.

ATHLETE: ${trainingProfile?.name || 'Athlete'}
Goal: ${trainingProfile?.goal || 'General fitness'}
Experience: ${trainingProfile?.experience || 'Intermediate'}
Days/week: ${trainingProfile?.daysPerWeek || 4}
Equipment: ${trainingProfile?.equipment || 'Commercial gym'}
Injuries: ${trainingProfile?.injuries || 'None'}
Favourite exercises: ${trainingProfile?.favourites || 'Not specified'}

APPROVED EXERCISES: ${yesExercises.length > 0 ? yesExercises.join(', ') : 'Standard gym exercises'}
AVOID: ${noExercises.length > 0 ? noExercises.join(', ') : 'None'}

WORKING WEIGHTS: ${Object.entries(trainingProfile?.workingWeights || {}).filter(([,v])=>v).map(([k,v])=>k+': '+v+'kg').join(', ') || 'Calibration week'}

STRUCTURE: 12 weeks, 3 blocks of 4 weeks. Week 12 = deload. Block 1 = Foundation (RPE 7-8, higher reps), Block 2 = Build (RPE 8, moderate reps), Block 3 = Peak (RPE 8-9, heavier). EVERY session must include a coreBlock (2-3 exercises: planks, dead bugs, pallof press, hanging leg raises, ab wheel — vary each session) and a cardioFinisher (8-12 min: assault bike, rower, treadmill, circuits — specify duration and intensity). Return ONLY valid JSON, no markdown:
{"programmeTitle":"string","goal":"string","weeks":[{"week":1,"block":1,"blockLabel":"Foundation","isDeload":false,"sessions":[{"day":1,"sessionName":"string","focus":"string","warmup":"string","exercises":[{"name":"string","sets":4,"reps":"8-10","rpe":7,"restSeconds":120,"weight":"string","coachingCue":"string"}],"coreBlock":[{"name":"string","sets":3,"reps":"45 sec","restSeconds":30,"coachingCue":"string"}],"cardioFinisher":{"description":"string"},"cooldown":"string"}]}]}

Build ALL 12 weeks. Each session 5-8 exercises. Make it excellent.`;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000);
      const data = await apiPost('/api/generate-programme', { prompt });
      clearTimeout(timeout);
      const prog = typeof data.programme === 'string' ? JSON.parse(data.programme) : data.programme;
      await saveProgramme(prog);
      window.location.reload();
    } catch (e) {
      console.error('Generate error:', e);
      if (e.name === 'AbortError') {
        setGenError('Request timed out. The server is warming up — please try again in 30 seconds.');
      } else {
        setGenError('Generation failed. Please try again in a moment.');
      }
    }
    setGenerating(false);
  }

  if (!programme || !programme.weeks) return (
    <div style={{ textAlign: 'center', padding: '3rem', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}><Icon name="clipboard" size={14} /></div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', color: 'var(--text)', marginBottom: '.75rem' }}>No programme yet</div>
      <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.7, marginBottom: '1.5rem' }}>
        Rico PT will build a complete 12-week periodised programme based on your profile, exercise preferences and health data. This takes about 30 seconds.
      </p>
      {genError && <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>{genError}</div>}
      {generating ? (
        <div>
          <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(0,174,239,0.2)', borderTop: '3px solid var(--primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
          <div style={{ fontSize: '.82rem', color: 'var(--text3)' }}>Building your 12-week programme… (~30 seconds)</div>
        </div>
      ) : (
        <button onClick={handleGenerate} style={{ padding: '.75rem 2rem', borderRadius: 'var(--radius)', border: 'none', background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.88rem', fontWeight: 500, cursor: 'pointer' }}>
          Generate My Programme →
        </button>
      )}
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );

  const weekData = programme.weeks.find(w => w.week === selectedWeek) || programme.weeks[0];
  const currentWeek = trainingProfile?.currentWeek || 1;

  const completedSessionKeys = new Set(
    sessions.filter(s => s.fromProgramme).map(s => `${s.week}-${s.sessionName}`)
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>{programme.programmeTitle || '12-Week Programme'}</h2>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>{programme.goal} · {trainingProfile?.daysPerWeek} days/week</p>
        </div>
        <div style={{ padding: '.4rem .9rem', borderRadius: '20px', background: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.2)', fontSize: '.72rem', color: 'var(--success)', fontWeight: 500 }}>
          Currently: Week {currentWeek}
        </div>
      </div>

      {/* Week selector */}
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.25rem', overflowX: 'auto', paddingBottom: '.4rem' }}>
        {programme.weeks.map(w => {
          const completedInWeek = w.sessions?.filter(s => completedSessionKeys.has(`${w.week}-${s.sessionName}`)).length || 0;
          const totalInWeek = w.sessions?.length || 0;
          const isDeload = w.isDeload;
          const isCurrent = w.week === currentWeek;
          return (
            <button key={w.week} onClick={() => { setSelectedWeek(w.week); setSelectedDay(null); }} style={{
              padding: '.5rem .85rem', borderRadius: 'var(--radius)', border: '1px solid', flexShrink: 0,
              borderColor: selectedWeek === w.week ? 'var(--primary)' : isCurrent ? 'var(--success)' : 'var(--border2)',
              background: selectedWeek === w.week ? 'rgba(0,174,239,0.1)' : isCurrent ? 'rgba(0,200,150,0.06)' : 'transparent',
              color: selectedWeek === w.week ? 'var(--primary)' : isCurrent ? 'var(--success)' : 'var(--text3)',
              fontSize: '.75rem', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center'
            }}>
              <div style={{ fontWeight: 600 }}>Wk {w.week}</div>
              <div style={{ fontSize: '.62rem', opacity: 0.8 }}>{isDeload ? 'Deload' : `B${w.block}`}</div>
              {totalInWeek > 0 && <div style={{ fontSize: '.6rem', color: completedInWeek === totalInWeek ? 'var(--success)' : 'var(--text3)' }}>{completedInWeek}/{totalInWeek}</div>}
            </button>
          );
        })}
      </div>

      {weekData && (
        <div>
          {/* Week header */}
          <div style={{ padding: '.75rem 1.1rem', background: weekData.isDeload ? 'rgba(240,160,80,0.06)' : 'rgba(0,174,239,0.06)', border: `1px solid ${weekData.isDeload ? 'rgba(240,160,80,0.2)' : 'rgba(0,174,239,0.15)'}`, borderRadius: 'var(--radius-lg)', marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '.72rem', fontWeight: 600, color: weekData.isDeload ? 'var(--warning)' : 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                Week {weekData.week} · {weekData.isDeload ? 'DELOAD' : `Block ${weekData.block} — ${weekData.blockLabel}`}
              </div>
              {weekData.isDeload && (
                <div style={{ fontSize: '.75rem', color: 'var(--text3)', fontWeight: 300, marginTop: '.2rem' }}>
                  Recovery week — 50% volume, same movements. Focus on technique and recovery.
                </div>
              )}
            </div>
          </div>

          {/* Sessions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '.85rem' }}>
            {(weekData.sessions || []).map((s, i) => {
              const key = `${weekData.week}-${s.sessionName}`;
              const done = completedSessionKeys.has(key);
              const isSelected = selectedDay === i;
              return (
                <div key={i}>
                  <div onClick={() => setSelectedDay(isSelected ? null : i)}
                    style={{ background: 'var(--bg-card)', border: `1px solid ${done ? 'rgba(0,200,150,0.3)' : isSelected ? 'rgba(0,174,239,0.3)' : 'var(--border2)'} `, borderRadius: 'var(--radius-lg)', overflow: 'hidden', cursor: 'pointer', transition: 'border-color .2s' }}>
                    <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.2rem' }}>
                          {done && <span style={{ fontSize: '.7rem', color: 'var(--success)' }}>✓</span>}
                          <div style={{ fontSize: '.88rem', fontWeight: 500, color: done ? 'var(--success)' : 'var(--text)' }}>{s.sessionName}</div>
                        </div>
                        <div style={{ fontSize: '.7rem', color: 'var(--text3)' }}>
                          Day {s.day} · {(s.exercises||[]).length} exercises
                          {s.coreBlock && s.coreBlock.length > 0 && <span style={{ color: '#a78bfa' }}> · {s.coreBlock.length} core</span>}
                          {s.cardioFinisher && <span style={{ color: 'var(--success)' }}> · cardio</span>}
                          {s.focus && <span> · {s.focus}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: '.7rem', color: 'var(--text3)' }}>{isSelected ? '▲' : '▼'}</span>
                    </div>

                    {isSelected && (
                      <div style={{ padding: '1rem 1.25rem' }}>
                        {s.warmup && <div style={{ fontSize: '.72rem', color: 'var(--warning)', marginBottom: '.75rem' }}><Icon name="flame" size={14} /> {s.warmup}</div>}
                        <div style={{ marginBottom: '.75rem' }}>
                          {(s.exercises||[]).map((ex, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.35rem 0', borderBottom: '0.5px solid var(--border2)' }}>
                              <div style={{ fontSize: '.78rem', color: 'var(--text)', fontWeight: 400 }}>{ex.name}</div>
                              <div style={{ fontSize: '.72rem', color: 'var(--primary)', fontWeight: 500, flexShrink: 0 }}>{ex.sets}×{ex.reps} @ RPE {ex.rpe}</div>
                            </div>
                          ))}
                        </div>
                        {s.coreBlock && s.coreBlock.length > 0 && (
                          <div style={{ marginBottom: '.75rem' }}>
                            <div style={{ fontSize: '.62rem', fontWeight: 600, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '.4rem' }}>Core</div>
                            {s.coreBlock.map((ex, j) => (
                              <div key={j} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.3rem 0', borderBottom: '0.5px solid var(--border2)' }}>
                                <div style={{ fontSize: '.75rem', color: 'var(--text2)', fontWeight: 300 }}>{ex.name}</div>
                                <div style={{ fontSize: '.7rem', color: '#a78bfa', fontWeight: 500, flexShrink: 0 }}>{ex.sets}×{ex.reps}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {s.cardioFinisher && s.cardioFinisher.description && (
                          <div style={{ padding: '.6rem .85rem', background: 'rgba(0,200,150,0.05)', border: '1px solid rgba(0,200,150,0.15)', borderRadius: 'var(--radius)', marginBottom: '.75rem', fontSize: '.73rem', color: 'var(--success)', lineHeight: 1.55 }}>
                            <span style={{ fontWeight: 600 }}>Cardio: </span>{s.cardioFinisher.description}
                          </div>
                        )}
                        {!done && (
                          <Btn variant="success" full onClick={() => onStartSession(s, weekData.week, weekData.block)}>
                            Start Session →
                          </Btn>
                        )}
                        {done && <div style={{ fontSize: '.78rem', color: 'var(--success)', textAlign: 'center', padding: '.5rem 0' }}>✓ Session completed</div>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gamification Dashboard ────────────────────────────────────────────────────

function GamDashboard({ gamification, sessions }) {
  const xp = gamification?.xp || 0;
  const badges = gamification?.badges || [];
  const streak = gamification?.streak || 0;
  const totalSessions = gamification?.totalSessions || 0;
  const totalPBs = gamification?.totalPBs || 0;
  const { current, next } = getLevel(xp);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>My Achievements</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>Track your progress, personal bests and earned badges</p>
      </div>

      {/* XP + Level */}
      <Card>
        <SLabel>Level & XP</SLabel>
        <XPBar xp={xp} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem', marginTop: '1.25rem' }}>
          {[
            { label: 'Total XP', value: xp, color: current.color },
            { label: 'Sessions', value: totalSessions, color: 'var(--primary)' },
            { label: 'Personal Bests', value: totalPBs, color: 'var(--success)' },
            { label: 'Day Streak', value: `${streak}<Icon name="flame" size={14} />`, color: 'var(--warning)' },
          ].map(stat => (
            <div key={stat.label} style={{ textAlign: 'center', padding: '.85rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)' }}>
              <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.8rem', fontWeight: 500, color: stat.color, lineHeight: 1 }}>{stat.value}</div>
              <div style={{ fontSize: '.65rem', color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', marginTop: '.3rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Badges */}
      <Card>
        <SLabel>Badges ({badges.length}/{BADGES.length})</SLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '.75rem' }}>
          {BADGES.map(badge => {
            const earned = badges.includes(badge.id);
            return (
              <div key={badge.id} style={{ padding: '1rem', background: earned ? 'var(--bg-elevated)' : 'rgba(255,255,255,0.02)', border: `1px solid ${earned ? 'rgba(167,139,250,0.3)' : 'var(--border2)'}`, borderRadius: 'var(--radius-lg)', textAlign: 'center', opacity: earned ? 1 : 0.45 }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '.4rem', filter: earned ? 'none' : 'grayscale(100%)' }}><Icon name={badge.icon} size={16} /></div>
                <div style={{ fontSize: '.78rem', fontWeight: 500, color: earned ? 'var(--text)' : 'var(--text3)', marginBottom: '.2rem' }}>{badge.label}</div>
                <div style={{ fontSize: '.65rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.4 }}>{badge.desc}</div>
                {earned && <div style={{ fontSize: '.65rem', color: '#a78bfa', fontWeight: 600, marginTop: '.4rem' }}>+{badge.xp} XP</div>}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ── AI PT (Rico PT Chat) ──────────────────────────────────────────────────────

function AIPT({ trainingProfile, sessions, healthData }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const name = trainingProfile?.name || 'athlete';
  const liftLog = buildLiftLog(sessions);
  const workingWeights = buildWorkingWeights(trainingProfile);

  const SUGGESTIONS = [
    'What should my next session be?',
    'Regenerate my full 12-week programme',
    'How is my progress looking?',
    'I want to increase intensity — adjust my programme',
    'My lower back is sore — what should I modify?',
  ];

  useEffect(() => {
    setMessages([{ role: 'assistant', content: `Hi ${name} — Rico PT here. I have your training profile, lift log and health data loaded.\n\nI can adjust your programme, plan your next session, analyse your progress, or modify workouts around injuries or health flags. What do you need?` }]);
  }, [name]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);
    try {
      const data = await apiPost('/api/pt', { messages: newMessages.map(m => ({ role: m.role, content: m.content })), trainingProfile, liftLog, workingWeights, healthData });
      setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue. Please try again.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 8rem)', maxHeight: 780, maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', marginBottom: '1.25rem', flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,200,150,0.15)', border: '1px solid rgba(0,200,150,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'var(--font-head)', fontSize: '.9rem', fontWeight: 500, color: 'var(--success)' }}>PT</span>
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', color: 'var(--text)' }}>Rico PT</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>{sessions.length} sessions logged · health data loaded</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '.85rem', marginBottom: '1rem' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: '.6rem', alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(0,200,150,0.15)', border: '1px solid rgba(0,200,150,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 600, color: 'var(--success)', flexShrink: 0 }}>PT</div>}
            <div style={{ maxWidth: '78%', background: msg.role === 'user' ? 'rgba(0,174,239,0.12)' : 'var(--bg-card)', border: msg.role === 'user' ? '1px solid rgba(0,174,239,0.25)' : '1px solid var(--border2)', borderRadius: msg.role === 'user' ? 'var(--radius-lg) 4px var(--radius-lg) var(--radius-lg)' : '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.1rem', fontSize: '.83rem', lineHeight: 1.7, color: 'var(--text)', fontWeight: 300, whiteSpace: 'pre-wrap' }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(0,200,150,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 600, color: 'var(--success)', flexShrink: 0 }}>PT</div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.2rem', display: 'flex', gap: '.3rem' }}>
              {[0,1,2].map(j => <div key={j} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', opacity: .5, animation: `bounce 1.2s ${j*.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length <= 1 && (
        <div style={{ flexShrink: 0, marginBottom: '.85rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => sendMessage(s)} style={{ padding: '.4rem .85rem', borderRadius: '20px', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text2)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 300, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--success)'; e.currentTarget.style.color = 'var(--success)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Ask Rico PT anything about your training…" rows={1}
            style={{ flex: 1, resize: 'none', padding: '.8rem 1rem', borderRadius: 'var(--radius-lg)', lineHeight: 1.5, minHeight: '46px', maxHeight: '120px', fontFamily: 'var(--font-body)', fontSize: '.85rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text)', outline: 'none' }} />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} style={{ padding: '.8rem 1.4rem', borderRadius: 'var(--radius-lg)', border: 'none', background: input.trim() && !loading ? 'var(--success)' : 'var(--bg-elevated)', color: input.trim() && !loading ? '#080f18' : 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: input.trim() && !loading ? 'pointer' : 'default', height: '46px', flexShrink: 0, transition: 'all .15s' }}>
            Send →
          </button>
        </div>
        <div style={{ marginTop: '.4rem', fontSize: '.65rem', color: 'var(--text3)', textAlign: 'center' }}>Rico PT uses your full lift log, health data and programme to give personalised coaching.</div>
      </div>
      <style>{'@keyframes bounce { 0%,60%,100%{transform:translateY(0);opacity:.5} 30%{transform:translateY(-4px);opacity:1} }'}</style>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────


// ── Session History Card ──────────────────────────────────────────────────────
function SessionHistoryCard({ session }) {
  const [expanded, setExpanded] = React.useState(false);
  const exs = session.exercises || [];
  const totalVolume = exs.reduce((sum, ex) => {
    const sets = ex.sets || 0;
    const reps = parseFloat(ex.reps) || 0;
    const weight = parseFloat(ex.weight) || 0;
    return sum + (sets * reps * weight);
  }, 0);

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', marginBottom: '.75rem', overflow: 'hidden' }}>
      <div onClick={() => setExpanded(v => !v)} style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '.88rem', fontWeight: 500, color: 'var(--text)' }}>{session.sessionName}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text3)', marginTop: '.15rem' }}>
              Week {session.week} · Block {session.block} · {exs.length} exercises
              {totalVolume > 0 && <span style={{ marginLeft: '.5rem', color: 'var(--success)' }}>· {Math.round(totalVolume).toLocaleString()}kg total volume</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: '.75rem', color: 'var(--text3)' }}>{session.date}</div>
          <span style={{ fontSize: '.7rem', color: 'var(--text3)' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border2)', padding: '1rem 1.25rem' }}>
          {exs.length > 0 ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '.5rem', marginBottom: '.5rem' }}>
                {['Exercise', 'Sets', 'Reps', 'Weight'].map(h => (
                  <div key={h} style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</div>
                ))}
              </div>
              {exs.map((ex, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 80px', gap: '.5rem', padding: '.4rem 0', borderBottom: i < exs.length - 1 ? '1px solid var(--border2)' : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: '.82rem', color: 'var(--text)', fontWeight: 400 }}>{ex.name}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text3)' }}>{ex.sets}</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text3)' }}>{ex.reps}</div>
                  <div style={{ fontSize: '.78rem', color: ex.weight ? 'var(--primary)' : 'var(--text3)', fontWeight: ex.weight ? 500 : 400 }}>
                    {ex.weight ? ex.weight + 'kg' : 'BW'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '.78rem', color: 'var(--text3)' }}>No exercise data recorded.</p>
          )}
          {session.sessionNotes && (
            <div style={{ marginTop: '.75rem', padding: '.6rem .85rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', fontSize: '.75rem', color: 'var(--text3)', fontStyle: 'italic' }}>
              <Icon name="edit" size={14} /> {session.sessionNotes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Training() {
  const [tab, setTab] = useState('programme');
  const [loading, setLoading] = useState(true);
  const [trainingProfile, setTrainingProfile] = useState(null);
  const [programme, setProgramme] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [toast, setToast] = useState(null);
  const [genError, setGenError] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [tp, prog, sess, blood, body, mental, profile, gam, np] = await Promise.all([
        loadTrainingProfile(), loadProgramme(), loadRecentSessions(30),
        loadAllBloodMarkers(), loadLatestMetrics('body'), loadLatestMetrics('mental'),
        loadLatestMetrics('profile'), loadGamification(), loadLatestMetrics('nutritionProfile')
      ]);
      setTrainingProfile(tp);
      setProgramme(prog);
      setSessions(sess);
      setGamification(gam);
      if (blood || body || mental) setHealthData({ blood: blood||{}, body: body||{}, mental: mental||{}, profile: profile||{}, nutritionProfile: np||{} });
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function handleSessionComplete({ pbs, newBadges, gamification: updatedGam }) {
    setActiveSession(null);
    setGamification(updatedGam);
    setSessions(prev => [{ date: todayStr(), ...activeSession }, ...prev]);
    if (pbs.length > 0 || newBadges.length > 0) {
      setToast({ pbs, newBadges });
    }
    loadAll();
  }

  if (loading) return <div style={{ padding: '2rem 0', color: 'var(--text3)' }}>Loading your training data…</div>;

  if (!trainingProfile?.setupComplete) {
    return <SetupFlow onComplete={(tp, prog) => { setTrainingProfile(tp); setProgramme(prog); setTab('programme'); }} healthData={healthData} />;
  }

  if (activeSession) {
    return (
      <GuidedWorkout
        session={activeSession.session}
        week={activeSession.week}
        block={activeSession.block}
        sessions={sessions}
        trainingProfile={trainingProfile}
        onComplete={handleSessionComplete}
        onCancel={() => setActiveSession(null)}
      />
    );
  }

  const xp = gamification?.xp || 0;
  const { current: level } = getLevel(xp);
  const streak = gamification?.streak || 0;

  const TABS = [
    { id: 'programme', label: 'My Programme' },
    { id: 'competition', label: 'Competition Plans' },
    { id: 'generate', label: 'Generate a Workout' },
    { id: 'wod', label: 'Team WOD' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'history', label: 'History' },
    { id: 'pt', label: 'Rico PT' },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {toast && <PBToast pbs={toast.pbs} newBadges={toast.newBadges} onDismiss={() => setToast(null)} />}

      {/* Header */}
      {genError && (
        <div style={{ padding: '.75rem 1rem', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', fontSize: '.78rem', marginBottom: '1rem' }}>
          {genError} <button onClick={() => setGenError('')} style={{ marginLeft: '.5rem', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '.78rem' }}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>My Training</h1>
          <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>
            {trainingProfile.goal} · {trainingProfile.daysPerWeek} days/week · Week {trainingProfile.currentWeek || 1}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center' }}>
          {streak > 1 && <div style={{ padding: '.35rem .85rem', borderRadius: '20px', background: 'rgba(240,160,80,0.1)', border: '1px solid rgba(240,160,80,0.2)', fontSize: '.72rem', fontWeight: 600, color: 'var(--warning)' }}>{streak}<Icon name="flame" size={14} /> Streak</div>}
          <div style={{ padding: '.35rem .85rem', borderRadius: '20px', background: level.color + '18', border: `1px solid ${level.color}44`, fontSize: '.72rem', fontWeight: 600, color: level.color }}>L{level.level} {level.label}</div>
          <button onClick={() => setTrainingProfile(null)} style={{ padding: '.35rem .85rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text3)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Edit Profile</button>
          <button onClick={async () => {
            if (!window.confirm('Start a new programme? Your current programme and history will be cleared.')) return;
            await saveProgramme(null);
            setProgramme(null);
            setTrainingProfile(null);
          }} style={{ padding: '.35rem .85rem', borderRadius: 'var(--radius)', border: '1px solid rgba(240,80,80,.3)', background: 'transparent', color: 'var(--danger)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Reset</button>
        </div>
      </div>

      {/* XP bar */}
      <Card style={{ marginBottom: '1.25rem' }}>
        <XPBar xp={xp} />
      </Card>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)', marginBottom: '1.75rem', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '.6rem 1rem', fontSize: '.8rem', color: tab === t.id ? 'var(--success)' : 'var(--text3)', cursor: 'pointer', border: 'none', background: 'none', borderBottom: tab === t.id ? '2px solid var(--success)' : '2px solid transparent', marginBottom: '-1px', fontFamily: 'var(--font-body)', fontWeight: tab === t.id ? 500 : 400, transition: 'color .15s', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'programme' && (
        <ProgrammeView
          programme={programme}
          trainingProfile={trainingProfile}
          sessions={sessions}
          gamification={gamification}
          onStartSession={(session, week, block) => setActiveSession({ session, week, block })}
        />
      )}
      {tab === 'competition' && <CompetitionPlans healthData={healthData} />}
      {tab === 'generate' && <GenerateWorkout trainingProfile={trainingProfile} healthData={healthData} />}
      {tab === 'wod' && <WOD />}
      {tab === 'achievements' && <GamDashboard gamification={gamification} sessions={sessions} />}
      {tab === 'history' && (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', fontWeight: 500, color: 'var(--text)', marginBottom: '1rem' }}>Session History ({sessions.length})</h2>
          {sessions.length === 0 ? <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No sessions logged yet. Complete your first session to see history here.</div> :
            sessions.map((s, i) => (
              <SessionHistoryCard key={i} session={s} />
            ))
          }
        </div>
      )}
      {tab === 'pt' && <AIPT trainingProfile={trainingProfile} sessions={sessions} healthData={healthData} />}
    </div>
  );
}
